import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useGetPaymentStatusQuery } from 'src/api/v2/payment-v2-ApiSlice';
import { useCancelStripeSubscriptionMutation, useChangeSubscriptionBankCardMutation } from 'src/api/v1/payment-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { PaymentSettingsIcon } from 'src/icons/PaymentSettingsIcon';
import { SubscriptionModal } from 'src/components/AppHeader/SubscriptionModal';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

type Props = {
    isProLicenseValid: boolean;
    proExpirationDate: string;
};

export const PaymentSettingsSection = ({ isProLicenseValid, proExpirationDate }: Props) => {
    const dispatch = useAppDispatch();
    const { data: paymentStatus } = useGetPaymentStatusQuery();
    const [cancelStripeSubscription, { isLoading: isCancelLoading }] = useCancelStripeSubscriptionMutation();
    const [changeSubscriptionBankCard, { isLoading: isChangeCardLoading }] = useChangeSubscriptionBankCardMutation();
    const [isSubscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);

    if (!paymentStatus) return null;

    const showError = () => dispatch(showSnackBar({
        text: ts('something_went_wrong_try_again', 'Something went wrong, please try again.'),
        severity: 'error',
    }));

    const hasProPackage = paymentStatus.is_active_subscription || paymentStatus.is_active_payment || isProLicenseValid;

    const handleChangeCard = () => {
        changeSubscriptionBankCard()
            .unwrap()
            .then((url) => {
                if (url) window.location.href = url;
            })
            .catch(showError);
    };

    const handleCancelSubscription = () => {
        cancelStripeSubscription()
            .unwrap()
            .then(() => setCancelDialogOpen(false))
            .catch(showError);
    };

    const renderStatus = (text: string, isPositive: boolean) => (
        <Typography
            variant="body2"
            color={isPositive ? theme.palette.success.main : theme.palette.error.main}
            sx={{ whiteSpace: 'pre-line' }}
        >
            {text}
        </Typography>
    );

    return (
        <SettingsSection
            title={ts('profile_settings_payment_settings', 'Payment settings')}
            titleIcon={<PaymentSettingsIcon />}
            titleAction={(
                <Button size="small" variant="outlined" sx={{ marginLeft: 'auto' }} onClick={() => setSubscriptionModalOpen(true)}>
                    {hasProPackage
                        ? ts('pro_package_button', 'Pro package')
                        : ts('upgrade_to_pro_button', 'Upgrade to Pro')}
                </Button>
            )}
        >
            <Stack spacing={2}>
                {paymentStatus.has_volume_license ? (
                    renderStatus(ts('profile_settings_pro_activated', 'Pro license is activated for this account'), true)
                ) : (
                    <>
                        {hasProPackage
                            ? renderStatus(`${ts('profile_settings_pro_activated_until', 'Pro license is activated for this account until: ')}\n${proExpirationDate}`, true)
                            : renderStatus(ts('profile_settings_pro_inactive', 'Pro license is inactive for this account.'), false)}
                        {paymentStatus.is_active_subscription && (
                            paymentStatus.has_ongoing_sub
                                ? renderStatus(ts('profile_settings_auto_subscription_active', 'Automatic subscription is active, your subscription will be renewed when the period ends.'), true)
                                : renderStatus(ts('profile_settings_auto_subscription_inactive', "Automatic subscription is inactive, your subscription won't be renewed when the period ends."), false)
                        )}
                    </>
                )}
                {paymentStatus.has_ongoing_sub && (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
                        <Stack spacing={1} sx={{ maxWidth: 360 }}>
                            <Button variant="contained" disabled={isChangeCardLoading} onClick={handleChangeCard}>
                                {ts('change_bank_card_details', 'Change bank card details')}
                            </Button>
                            <Typography variant="body2" color={theme.palette.text.secondary}>
                                {ts('modify_card_details_or_retry_payment_text', 'Modify bank card details or retry payment. In case of unsuccessful payment, we will automatically retry to deduct the amount in the following days.')}
                            </Typography>
                        </Stack>
                        <Button variant="outlined" color="error" onClick={() => setCancelDialogOpen(true)}>
                            {ts('cancel_subscription', 'Cancel subscription')}
                        </Button>
                    </Stack>
                )}
            </Stack>
            <SubscriptionModal open={isSubscriptionModalOpen} onClose={() => setSubscriptionModalOpen(false)} />
            <BaseDialog
                open={isCancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                title={ts('cancel_subscription', 'Cancel subscription')}
                maxWidth="xs"
                cancelLabel={ts('plt_cancel', 'Cancel')}
                actionLabel={ts('plt_confirm', 'Confirm')}
                actionDisabled={isCancelLoading}
                onAction={handleCancelSubscription}
            >
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {`${ts('you_want_to_cancel_subscription_question', 'Are you sure you want to cancel your subscription')}?`}
                </Typography>
            </BaseDialog>
        </SettingsSection>
    );
};
